/**
 * waitRules — the ONE wait rule (Practice Ops plan §2, 2026-10-30).
 *
 *   waitStart = max(scheduled, arrived)      the clock starts at the LATER of the booked time and arrival
 *   waitEnd   = seated                       the operatory drop; Ready NEVER stops it
 *   waitMin   = whole minutes from waitStart to waitEnd (floor, never negative)
 *   lateMin   = arrived − scheduled when > 0, else 0
 *   inChairMin= checkout − seated
 *   level     = 'ok' | 'amber' (>= 1x the threshold) | 'red' (>= 2x)
 *
 * MIRROR of teamio-serverless-api practice-pulse-service/lib/wait-rules.js. BOTH run the same shared table,
 * teamio-serverless-api/practice-pulse-service/tests/fixtures/wait-rules.fixtures.json — the agreement test is
 * cypress/node-tests/practice-ops-wait-rules.test.js. Change a rule in either repo and the fixture changes with it,
 * or one side goes red. The live board badge (browser clock, `now` passed) and the server report (`now` omitted)
 * call the same functions.
 *
 * NULLS: a time that is missing or unparsable is `null`, and any result that needs it is `null` — never NaN,
 * never 0 standing in for "unknown". A visit with no scheduled time has no waitStart, so its wait is null.
 *
 * ⚠️ Keep this file import-free (no import/require): the node-tests execute this exact file.
 * Times are a Date, epoch milliseconds, or an ISO string with a zone (Z / +hh:mm).
 */

const MS_PER_MINUTE = 60 * 1000;

/** Epoch milliseconds, or null when the value is missing / unparsable. */
export function toMs(value) {
  if (value === null || value === undefined || value === '') return null;
  let ms;
  if (typeof value === 'number') ms = value;
  else if (value instanceof Date) ms = value.getTime();
  else if (typeof value === 'string') ms = Date.parse(value);
  else return null;
  return Number.isFinite(ms) ? ms : null;
}

/** The later of the booked time and the arrival, in ms. null when either is unknown. */
export function waitStartMs(scheduled, arrived) {
  const s = toMs(scheduled);
  const a = toMs(arrived);
  if (s === null || a === null) return null;
  return Math.max(s, a);
}

/**
 * Whole minutes waited. `seated` ends the wait; with no `seated`, a caller that passes `now` gets the LIVE
 * wait so far (the board badge), and one that does not gets null (the report: never seated ⇒ no wait).
 * Negative (seated before the booked time, or clock skew) clamps to 0.
 */
export function waitMin(scheduled, arrived, seated, now) {
  const start = waitStartMs(scheduled, arrived);
  if (start === null) return null;
  const end = toMs(seated) !== null ? toMs(seated) : toMs(now);
  if (end === null) return null;
  return Math.max(0, Math.floor((end - start) / MS_PER_MINUTE));
}

/** Whole minutes arrived after the booked time; 0 when on time or early. null when either is unknown. */
export function lateMin(scheduled, arrived) {
  const s = toMs(scheduled);
  const a = toMs(arrived);
  if (s === null || a === null) return null;
  const diff = a - s;
  return diff > 0 ? Math.floor(diff / MS_PER_MINUTE) : 0;
}

/** Whole minutes between seated and checkout. null when either is unknown or checkout is BEFORE seated (bad data). */
export function inChairMin(seated, checkout) {
  const s = toMs(seated);
  const c = toMs(checkout);
  if (s === null || c === null) return null;
  const diff = c - s;
  return diff < 0 ? null : Math.floor(diff / MS_PER_MINUTE);
}

/**
 * 'ok' | 'amber' | 'red' for a wait against the office's threshold (minutes, number or numeric string — it has
 * been stored as "5"). No wait (null) ⇒ null. A wait with no usable threshold (missing, 0, negative, non-numeric)
 * ⇒ 'ok': "not configured" never flags, as the classic board's isOverThreshold.
 */
export function level(waitMinutes, thresholdMin) {
  if (typeof waitMinutes !== 'number' || !Number.isFinite(waitMinutes)) return null;
  const limit = Number(thresholdMin);
  if (thresholdMin === null || thresholdMin === undefined || thresholdMin === '' || !Number.isFinite(limit) || limit <= 0) return 'ok';
  if (waitMinutes >= 2 * limit) return 'red';
  if (waitMinutes >= limit) return 'amber';
  return 'ok';
}

/** All four numbers for one visit from its event times. `now` only matters for a live (not yet seated) wait. */
export function evaluateVisit({ scheduledAt, arrivedAt, seatedAt, checkoutAt, thresholdMin, now }) {
  const wait = waitMin(scheduledAt, arrivedAt, seatedAt, now);
  return {
    waitMin: wait,
    lateMin: lateMin(scheduledAt, arrivedAt),
    inChairMin: inChairMin(seatedAt, checkoutAt),
    level: level(wait, thresholdMin),
  };
}
